import {
  Button,
  Col,
  Form,
  Input,
  Row,
  Spin,
  Switch,
  Tooltip,
  TreeSelect,
  Typography,
  message,
} from "antd";
import { Alert } from "antd-v5";
import { FormInstance } from "antd/lib/form";
import classNames from "classnames";
import AppLoader from "components/AppLoader";
import AuthorisedContainer from "components/AuthorisedContainer";
import CustomCollapse from "components/CustomCollapse";
import { InfoIcon } from "components/Icons";
import Vocabulary, { getVocabulary } from "components/Vocabulary";
import { SEVERITY_COLOR } from "constants/Layout";
import { PermissionSetMap } from "constants/RBACConstants";
import {
  getFeatureFlags_UNSAFE,
  useFeatureFlag,
} from "contexts/FeatureFlagContext";
import useResolvedFindingStatusTemp from "hooks/useResolvedFindingStatusTemp";
import _, {
  capitalize,
  isArray,
  isEmpty,
  isNil,
  isString,
  isUndefined,
} from "lodash";
import moment from "moment";
import React, {
  ForwardedRef,
  MutableRefObject,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import TicketsCreationAPI from "restAPI/TicketsCreationAPI";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import { disablePastDates } from "utils/dateUtils";
import {
  ALL_DATE_FIELDS,
  CUSTOM_FIELDS_SINGLE_SELECTION,
  CUSTOM_FIELDS_WITH_OPTIONS,
  CUSTOM_FIELDS_WITH_SEARCH,
  CUSTOM_FIELDS_WITH_USER_API,
  CUSTOM_FIELD_TYPE,
  FieldTypes,
  PROVIDE_SELECTED_VALUES,
  TIME_SUPPORTED_DATE_FIELDS,
  getField,
} from "utils/formUtils";
import { getUrlSearch } from "utils/urlUtil";
import {
  FINDING_STATUS_DISPLAY_TEXT,
  SEVERITY_NAMES,
} from "views/Findings/Constants";

import ACDropdown, { ACDropdownItemProps } from "ac-components/ACDropdown";
import CustomFieldsIcon from "ac-components/ac-icons/CustomFieldsIcon";
import RefreshIcon from "ac-components/ac-icons/RefreshIcon";

import {
  findingFieldValidator,
  templateFieldsWithBrackets,
} from "./TicketTemplateForm";
import { parseAreaOptions } from "./azureBoard/AzureBoardMappingForm";
import {
  CMDB_SUPPORTED_TOOLS,
  CUSTOM_FIELDS_RISK_RATING_MAPPING_KEYS,
  CUSTOM_FIELDS_VARIABLE_FIELD_OPTIONS,
  CUSTOM_SEVERITY_MAPPING_SUPPORTED_TOOLS,
  FRESHSERVICE_CONFIG_DEFAULT_VALUES,
  FR_CUSTOM_CATEGORY_FIELDS,
  GenericIntegrationTicketDefaultValues,
  IdNameType,
  IntegrationCustomFieldTypes,
  LabelValueType,
  NULL_PROJECT_KEY_SUPPORTED_TICKETING_TOOLS,
  TEMPLATE_SUPPORTED_INTEGRATIONS,
  genericIntegrationTicketProductSubproductAppendAll,
} from "./genericIntegrationTicketConstants";
import { getTicketSystemName } from "./new/util";

export function getFreshServiceDefaultData(
  data: GenericIntegrationTicketDefaultValues = {},
): GenericIntegrationTicketDefaultValues {
  data = {
    ...data,
    labels: data?.labels ?? ["Armorcode_Associated_Finding"],
    ...data.properties,
    subProduct: !isEmpty(data?.subProductIds) ? data?.subProductIds : ["all"],
    product: data?.product ?? ["all"],
    ...(data.transitions
      ? { transitions: JSON.stringify(data.transitions) }
      : {}),
  };

  const defaultValues = { ...FRESHSERVICE_CONFIG_DEFAULT_VALUES };
  Object.keys(data).forEach((key) => {
    // TODO resolve TS
    // @ts-expect-error
    if (_.isPlainObject(data[key]) && data[key].id) {
      // @ts-expect-error
      defaultValues[key] = data[key].id;
      // @ts-expect-error
    } else if (data?.[key]) {
      // @ts-expect-error
      defaultValues[key] = data?.[key];
    }
  });
  return defaultValues;
}

const getIntegrationName = (toolName: string) => {
  // optional chaining for safety if toolName is not available in any case
  const formattedToolName = toolName?.replace(/ /g, "_");
  switch (formattedToolName) {
    case "VERSION_ONE":
      return "Version One";
    case "AZURE_BOARD":
      return "Azure Board";
    default:
      return getTicketSystemName(formattedToolName);
  }
};

export function fetchConfigAndRedirectToIntegration({
  toolName,
  productId,
  subProductId,
}: {
  toolName: string;
  productId: number;
  subProductId: number;
}) {
  const closeMessage = message.loading("Pulling source details");

  const isJiraConfigPaginated = toolName === "JIRA";
  ToolsIntegrationsAPI.getTicketSystemsByProductSubproduct({
    product: [productId],
    subProduct: [subProductId],
  })
    .promise.then((response) => {
      const config = response[0];
      const integrationName = getIntegrationName(toolName);
      const defaultActiveLoginConfigId = config.loginConfigId;

      window.open(
        `${window.location.origin}/#/integrations/${integrationName}?${getUrlSearch(
          {
            filters: {
              defaultActiveLoginConfigId: [defaultActiveLoginConfigId],
              defaultProductFilter:
                isJiraConfigPaginated === true
                  ? [config.product?.id]
                  : [config.product?.name],
              defaultSubproductFilter:
                isJiraConfigPaginated === true
                  ? [config.subProduct?.id]
                  : [config.subProduct?.name],
            },
          },
        )}`,
      );
    })
    .finally(() => {
      closeMessage();
    });
}

export const useUnifiedSingleTemplateOptions = () => {
  const [unifiedSingleDefaultTemplate, setUnifiedSingleDefaultTemplate] =
    useState();
  const getUnifiedSingleTemplates = useCallback(() => {
    const { promise, cancel } =
      ToolsIntegrationsAPI.getUnifiedTicketTemplatesByType({ type: "SINGLE" });
    const updatedPromise = promise.then((res) => {
      const mutatedOptions = res.map((option: any) => ({
        label: option.defaultTemplate ? `(Default)${option.name}` : option.name,
        value: option.id,
      }));
      const defaultSingleTemplateId = res.find(
        (t: any) => t.defaultTemplate,
      )?.id;
      setUnifiedSingleDefaultTemplate(defaultSingleTemplateId);
      return mutatedOptions;
    });

    return { promise: updatedPromise, cancel };
  }, []);

  return { getUnifiedSingleTemplates, unifiedSingleDefaultTemplate };
};

export const useUnifiedMultiTemplateOptions = () => {
  const [unifiedMultiDefaultTemplate, setUnifiedMultiDefaultTemplate] =
    useState();
  const getUnifiedMultiTemplates = useCallback(() => {
    const { promise, cancel } =
      ToolsIntegrationsAPI.getUnifiedTicketTemplatesByType({ type: "MULTI" });
    const updatedPromise = promise.then((res) => {
      const mutatedOptions = res.map((option: any) => ({
        label: option.defaultTemplate ? `(Default)${option.name}` : option.name,
        value: option.id,
      }));
      const defaultTemplateId = res.find((t: any) => t.defaultTemplate)?.id;
      setUnifiedMultiDefaultTemplate(defaultTemplateId);
      return mutatedOptions;
    });

    return { promise: updatedPromise, cancel };
  }, []);

  return { getUnifiedMultiTemplates, unifiedMultiDefaultTemplate };
};

//returns resolved finding statuses names with vocabulary
export const ResolvedFindingStatusNames = (): JSX.Element => {
  // use useResolvedFindingStatus hook once DYNAMIC_STATUS_CHECK is GA and delete the temp hook
  const resolvedFindingStatus = useResolvedFindingStatusTemp();
  return (
    <>
      {_.map(resolvedFindingStatus, (s, i) => (
        <span>
          {FINDING_STATUS_DISPLAY_TEXT[s]}
          {i !== resolvedFindingStatus.length - 1 && ", "}
        </span>
      ))}
    </>
  );
};

export async function generateResponse(
  data: GenericIntegrationTicketDefaultValues,
  featureFlags: any,
  toolName: string,
  id?: number,
  loginConfigId?: number,
  url?: string,
  customFieldsData?: any,
) {
  const severities: any = Object.keys(SEVERITY_COLOR);
  const configValues: GenericIntegrationTicketDefaultValues = _.omit(
    data,
    severities,
  );
  configValues.enabled = true;
  configValues.properties = {};
  severities.forEach((s: any) => {
    //TODO resolve TS
    // @ts-expect-error
    configValues.properties[s] = data[s];
  });
  if (!isUndefined(configValues.transitions)) {
    configValues.transitions = isEmpty(configValues.transitions)
      ? []
      : JSON.parse(configValues.transitions);
  }
  configValues.loginConfigId = loginConfigId;
  if (url) {
    configValues.url = url;
  }

  const isAllProduct = configValues.product?.[0] === "all";
  const isAllSubproduct = configValues.subProduct?.[0] === "all";
  if (isAllSubproduct) {
    delete configValues.subProduct;
  }
  if (isAllProduct) {
    delete configValues.product;
  }
  if (isAllProduct && isAllSubproduct) {
    configValues.configurationType = "GLOBAL";
  } else if (
    (!isAllProduct && isAllSubproduct) ||
    (configValues.product && configValues.product?.length > 1)
  ) {
    configValues.configurationType = "PRODUCT";
  } else {
    configValues.configurationType = "SUBPRODUCT";
  }

  if (customFieldsData) {
    const { inputCustomFields, customFieldState: fieldsChecked } =
      customFieldsData;
    configValues.customFields = [];
    _.forEach(inputCustomFields, (cstmField: IntegrationCustomFieldTypes) => {
      let fieldValue: any | any[];
      const customFieldKey =
        cstmField.key! as keyof GenericIntegrationTicketDefaultValues;
      fieldValue = isArray(data[customFieldKey])
        ? (data[customFieldKey] as string[] | number[])?.join()
        : data[customFieldKey];

      // cascadingselect: value format `parent:child`
      if (
        cstmField.type === "cascadingselect" &&
        _.isArray(data[customFieldKey])
      ) {
        const cascadingselectValue = data[customFieldKey] as string[];
        fieldValue = _.isUndefined(cascadingselectValue?.[1])
          ? `${cascadingselectValue[0]}`
          : `${cascadingselectValue[0]}:${cascadingselectValue[1]}`;
      }
      if (cstmField.type === "datepicker" || cstmField.type === "dateTime") {
        fieldValue = moment(fieldValue).isValid()
          ? moment(fieldValue).format("YYYY-MM-DD")
          : fieldValue;
      }

      //setup sevrity/priority group field payload
      if (
        featureFlags.TICKETING_CUSTOM_SEVERITY_RISK_RATING &&
        CUSTOM_SEVERITY_MAPPING_SUPPORTED_TOOLS.includes(
          toolName?.toLowerCase(),
        )
      ) {
        const mappings = getSeverityFieldMappingsPayload(cstmField, data);
        if (!isEmpty(mappings)) {
          cstmField.mappings = mappings;
        }
      }
      // if fieldValue is mismatch with defaultValue means defaultValue is from template
      const field = {
        ...cstmField,
        active: !!fieldsChecked[customFieldKey],
        defaultVal: fieldValue,
      };
      if (
        (CUSTOM_FIELDS_WITH_SEARCH.includes(cstmField.type!) ||
          CUSTOM_FIELDS_WITH_USER_API.includes(cstmField.type!) ||
          PROVIDE_SELECTED_VALUES.includes(cstmField.type!)) &&
        !isEmpty(data[customFieldKey]) &&
        !isString(data[customFieldKey])
      ) {
        const { fieldValue: value, selectedValues } =
          getCustomFieldSelectedValuesPayload(data, cstmField);
        field.defaultVal = value;
        field.selectedValues = selectedValues;
        fieldValue = value;
      }
      if (_.isBoolean(fieldValue) || !_.isEmpty(fieldValue)) {
        configValues[customFieldKey] = fieldValue;
        configValues.customFields?.push(field);
      }
    });
  }

  return {
    configValues,
  };
}

export function generateJiraCustomFields({
  customFields,
  form,
  onValuesChange,
  customFieldsValues,
  isFindingTicketPage = false,
  showFieldHeaders = false,
  toolName,
}: {
  customFields: any;
  form: FormInstance;
  onValuesChange: (value: Record<string, string | undefined>) => void;
  customFieldsValues?: any;
  isFindingTicketPage?: boolean;
  showFieldHeaders?: boolean;
  toolName?: string;
}) {
  const { UNIFIED_TICKET_TEMPLATE, TICKETING_CUSTOM_SEVERITY_RISK_RATING } =
    getFeatureFlags_UNSAFE();
  return (
    <>
      {showFieldHeaders && (
        <Row className={`jira-custom-field custom-field-header`}>
          <Col span={6}>
            <span>Ticket Field</span>
          </Col>
          <Col span={6}>
            <span>Value</span>
          </Col>
          <Col span={2} style={{ margin: 0 }}>
            <span>Enable?</span>
          </Col>
        </Row>
      )}
      {customFields.map((field: any, i: number) => {
        const fieldWithFindingVariableSelect =
          getFieldWithFindingVariablesSelect({
            field,
            form,
            onValuesChange,
            customFieldsValues,
            isFindingTicketPage,
            toolName,
          });
        // The $fieldWithFindingVariableSelect function returns an array.
        // Now, it is necessary to display severity and priority fields
        // below the custom field.
        //
        // To achieve this:
        // - Place the severity and priority fields in a separate row
        //   after the custom field and the switch.
        // - This ensures that the existing rows and columns for the
        //   custom field remain undisturbed.
        const customFieldwithVariableInputWithSwitch =
          !isEmpty(fieldWithFindingVariableSelect) &&
          fieldWithFindingVariableSelect[0];
        const severityMappingGroupFields =
          !isEmpty(fieldWithFindingVariableSelect) &&
          fieldWithFindingVariableSelect[1];
        return (
          <>
            <Row
              className={classNames("jira-custom-field", {
                [`custom-field-${i}`]: !showFieldHeaders || i !== 0,
              })}
            >
              <Col span={11}>
                <div
                  className={classNames("title text-bold m-b-xs", {
                    "required-mark": field.requireField,
                  })}
                >
                  {field.title}{" "}
                  {field.name === "parent" && (
                    <Tooltip title="Please provide ticket key only.">
                      <InfoIcon />
                    </Tooltip>
                  )}
                </div>
                {field.description && (
                  <div className="description">{field.description}</div>
                )}
              </Col>
              {UNIFIED_TICKET_TEMPLATE
                ? getField(customFieldwithVariableInputWithSwitch, i, 11)
                : getField(field, i, 11)}
              {field.switchProps && (
                <Col span={2} className="field-selection">
                  <Switch size="small" {...field.switchProps} />
                </Col>
              )}
            </Row>
            {severityMappingGroupFields &&
              TICKETING_CUSTOM_SEVERITY_RISK_RATING && (
                <Row className={`jira-custom-field-severity-field`}>
                  <Col span={24}>{getField(severityMappingGroupFields, i)}</Col>
                </Row>
              )}
          </>
        );
      })}
    </>
  );
}

//TODO: need to resolve TS
export const RenderCustomFields = forwardRef(
  (
    {
      // @ts-expect-error
      runbook = false,
      // @ts-expect-error
      customFieldsValueRef,
      // @ts-expect-error
      useValueField = false,
      // @ts-expect-error
      form,
      // @ts-expect-error
      loginConfigId,
      // @ts-expect-error
      ticketConfigurationId,
      // @ts-expect-error
      initialValues,
      // @ts-expect-error
      configId,
      // @ts-expect-error
      changeMode,
      // @ts-expect-error
      withCollapse = true,
      // @ts-expect-error
      innerRef = null,
      // @ts-expect-error
      renderCustomFields,
      // @ts-expect-error
      fieldMessage = "Please select Project Key and Issue Type",
      // @ts-expect-error
      toolName,
      // @ts-expect-error - resolve error: Property 'showFieldDefaultValue' does not exist on type { children?: ReactNode; }
      showFieldDefaultValue = true,
      ...restProps
    },
    ref: ForwardedRef<any>,
  ) => {
    const featureFlags = useFeatureFlag();
    const initialData = useRef();
    const [inputCustomFields, setInputCustomFields] = useState([]);
    const [customFieldState, setCustomFieldState] = useState({});
    const [loading, setLoading] = useState(false);

    const [selectedFRCategory, setSelectedFRCategory] = useState("");
    const [selectedFRSubCategory, setSelectedFRSubCategory] = useState("");
    const apiParamRef = useRef({});
    const thisRef: MutableRefObject<any> = useRef(innerRef);
    const categoryFieldRef: MutableRefObject<any> = useRef({});
    const checkEmptyField = (value: string) =>
      _.isUndefined(value) || _.isNull(value);

    // Set initialData only once when initialValues are loaded and valid.
    // This avoids overwriting the ref on every render and ensures we capture the first meaningful state.
    useEffect(() => {
      if (
        !initialData.current &&
        (initialValues?.projectKey || initialValues?.issueType)
      ) {
        initialData.current = initialValues;
      }
    }, [initialValues.projectKey, initialValues.issueType]);

    const text = useMemo(() => {
      if (loading) {
        return;
      }
      let text = "No Custom Fields.";
      if (loginConfigId) {
        if (
          !NULL_PROJECT_KEY_SUPPORTED_TICKETING_TOOLS.includes(
            toolName?.toLowerCase(),
          ) &&
          (!initialValues.projectKey || !initialValues.issueType)
        ) {
          text = fieldMessage;
        } else if (!initialValues.form && !initialValues.issueType) {
          text = "Please select Form and Issue Type";
        }
      }
      return <Typography.Text strong>{text}</Typography.Text>;
    }, [loginConfigId, initialValues, fieldMessage, loading, toolName]);

    const resetFields = useCallback(() => {
      setInputCustomFields([]);
      setCustomFieldState({});
      apiParamRef.current = {};
    }, []);

    const onValuesChange = useCallback(
      (current) => {
        if (customFieldsValueRef) {
          customFieldsValueRef.current = {
            ...customFieldsValueRef.current,
            ...current,
          };
        }
        _.forEach(current, (value, key) => {
          setCustomFieldState((customFieldState) => ({
            ...customFieldState,
            [key]: _.isBoolean(value) ? value : !_.isEmpty(value),
          }));
        });
      },
      [customFieldsValueRef],
    );

    useEffect(() => {
      if (!checkEmptyField(loginConfigId)) {
        const {
          projectKey,
          organisation,
          issueType,
          issueTypeId,
          form: formField,
        } = initialValues;
        const isToolSupportsNullProjectKey =
          NULL_PROJECT_KEY_SUPPORTED_TICKETING_TOOLS.includes(
            toolName?.toLowerCase(),
          ); /*Zendesk does not have projectKey*/
        setInputCustomFields([]);

        let shouldLoadFeilds = false;
        if (toolName?.toLowerCase() === "asana") {
          shouldLoadFeilds = organisation && issueType;
        } else {
          shouldLoadFeilds =
            (projectKey && issueType) || (formField && issueType);
        }

        if (isToolSupportsNullProjectKey && !formField) {
          return;
        } else if (shouldLoadFeilds) {
          setLoading(true);
          let isEdit = changeMode;
          if (isEdit && !thisRef.current) {
            thisRef.current = true;
            isEdit = false;
          }
          // pass configId if fetching existing custom fields config
          // pass loginConfigId, issueType and project key if issueType/projectKey is changed or not in edit mode'
          const isFormChanged =
            formField !==
            // TODO: resolve TS error: resolution pending till enhancing custom field code
            // @ts-expect-error
            initialData.current?.form; /* this check required for Zendesk */
          const isIssueTypeChanged =
            // TODO: resolve TS error: resolution pending till enhancing custom field code
            // @ts-expect-error
            issueType !== initialData.current?.issueType;
          const isProjectKeyChanged =
            // TODO: resolve TS error: resolution pending till enhancing custom field code
            // @ts-expect-error
            projectKey !== initialData.current?.projectKey;

          const shouldUpdateConfig =
            !configId ||
            (isEdit &&
              ((isToolSupportsNullProjectKey && isFormChanged) ||
                isIssueTypeChanged)) ||
            isIssueTypeChanged ||
            isProjectKeyChanged;
          const params = shouldUpdateConfig
            ? {
                loginConfigId,
                ...(toolName === "ASANA" ? { organisation } : { projectKey }),
                issueType,
                ...(issueTypeId ? { issueTypeId } : {}),
                form: formField,
              }
            : { configId, form: formField };
          // Archer only supports issueTypeId in params
          // improve this logic
          const updatedParams =
            toolName === "ARCHER" ? _.omit(params, "issueType") : params;
          const { promise, cancel } =
            TicketsCreationAPI.getCustomTicketFields(updatedParams);
          promise
            .then(({ fields }) => {
              if (_.isArray(fields)) {
                const customState: any = {};
                let subcategoryFieldIndex: number = 0;
                const subCategoryFields: any[] = [];
                fields.forEach((field: any, index: number) => {
                  // @ts-expect-error
                  const obj = (initialData.current?.customFields || []).find(
                    (f: any) => f.key === field.key,
                  );
                  Object.assign(field, {
                    active: obj?.active || field.isRequired,
                  });
                  const value = useValueField ? field.value : field.defaultVal;
                  // if field.defaultVal exist, set form field value
                  // defaultVale only need to show on integration mapping form not on runbook
                  // this is behaviour is required for duedate type field
                  if (field.defaultVal && showFieldDefaultValue) {
                    form.setFieldsValue({ [field.key]: field.defaultVal });
                  }
                  // set false for null boolean type values
                  // required field throws validation error
                  // when user tries to save value initially
                  // without changing the checkbox
                  if (
                    field.isRequired &&
                    field.type === "boolean" &&
                    isNil(value)
                  ) {
                    form.setFieldsValue({ [field.key]: false });
                  }
                  if (field.active) {
                    customState[field.key] = field.active;
                  }

                  //setting up FreshService subcategory and subcategory item fields
                  //based on category field
                  if (field.name === "category") {
                    subcategoryFieldIndex = index;
                    categoryFieldRef.current = field;
                    if (field.nestedFields) {
                      //subcategory
                      subCategoryFields.push(field.nestedFields);
                    }
                    if (field.nestedFields.nestedFields) {
                      //subcategory item
                      subCategoryFields.push(field.nestedFields.nestedFields);
                    }
                  }
                });
                //add subcategory and category item fields below category field
                if (!isEmpty(subCategoryFields)) {
                  fields.splice(
                    subcategoryFieldIndex + 1,
                    0,
                    ...subCategoryFields,
                  );
                }
                // TODO: resolve TS error: resolution pending till enhancing custom field code
                // @ts-expect-error
                if (!_.isEmpty(initialData.current?.customFields)) {
                  fields.forEach((customField) => {
                    // TODO: resolve TS error: resolution pending till enhancing custom field code
                    // @ts-expect-error
                    const obj = initialData.current.customFields.find(
                      (f: any) => f.key === customField.key,
                    );
                    if (obj) {
                      const value = useValueField
                        ? obj.value || ""
                        : obj.defaultVal || "";
                      // check type in lowercase, since JIRA has datetime and Azure has dateTime
                      const typeLowerCase = obj.type?.toLowerCase();
                      const isTextField =
                        typeLowerCase === "textarea" ||
                        typeLowerCase === "string" ||
                        typeLowerCase === "html" ||
                        _.isUndefined(typeLowerCase);

                      let fieldValue =
                        ALL_DATE_FIELDS.indexOf(typeLowerCase) === -1 &&
                        !isTextField &&
                        value.includes(",")
                          ? _.compact(value.split(","))
                          : value;
                      // in case of zendesk and asana custom fields, do not set defaultValue
                      Object.assign(customField, {
                        ...(!(
                          toolName?.toLowerCase() === "zendesk" ||
                          toolName?.toLowerCase() === "asana"
                        )
                          ? {
                              defaultValue: _.isEmpty(customField.defaultValue)
                                ? { id: fieldValue, name: fieldValue }
                                : customField.defaultValue,
                            }
                          : {}),
                        active: innerRef ? obj.active : customField.active,
                        selectedValues: runbook
                          ? obj.selectedValues
                          : customField.selectedValues,
                      });
                      if (customField.active) {
                        customState[customField.key] = customField.active;
                      }
                      if (
                        (typeLowerCase === "datetime" ||
                          ALL_DATE_FIELDS.indexOf(obj.type) !== -1) &&
                        fieldValue &&
                        typeof fieldValue === "string"
                      ) {
                        fieldValue = moment(fieldValue).isValid()
                          ? moment(fieldValue)
                          : fieldValue;
                      }
                      if (
                        obj.type === "boolean" &&
                        typeof fieldValue === "string"
                      ) {
                        fieldValue = fieldValue === "true";
                      }
                      let _value = fieldValue;
                      if (
                        //TODO: need to resolve typesc
                        // @ts-expect-error
                        CUSTOM_FIELD_TYPE[customField.type] &&
                        ["option", "array", "any"].includes(
                          customField.dataType,
                        )
                      ) {
                        _value = _.isEmpty(fieldValue) ? [] : fieldValue;
                      }
                      if (customField.type === "cascadingselect") {
                        _value = _.split(fieldValue, ":");
                      }
                      if (customFieldsValueRef) {
                        customFieldsValueRef.current = {
                          ...customFieldsValueRef.current,
                          ...{ [customField.key]: _value },
                        };
                      }

                      //set initial form fields values for FreshService subcategory and subcategory item fields
                      if (!isEmpty(subCategoryFields)) {
                        subCategoryFields.forEach((subcategoryField: any) =>
                          form.setFieldsValue({
                            [subcategoryField.key]: subcategoryField.defaultVal,
                          }),
                        );
                      }
                      //get and set severity/priority form field values from custom field mappings object
                      // based on the obj.defaultVal
                      if (featureFlags.TICKETING_CUSTOM_SEVERITY_RISK_RATING) {
                        const isRiskRatingField =
                          value &&
                          isString(value) &&
                          value.indexOf("finding.riskRating") !== -1;
                        const isSeverityValueField =
                          value &&
                          isString(value) &&
                          value.indexOf("finding.severity.value") !== -1;
                        if (isRiskRatingField && !isEmpty(obj.mappings)) {
                          for (const riskTitleKey of CUSTOM_FIELDS_RISK_RATING_MAPPING_KEYS) {
                            if (obj.mappings[getVocabulary(riskTitleKey)]) {
                              const keyName = `${obj.key}-${getVocabulary(riskTitleKey)}`;
                              const value =
                                obj.mappings[getVocabulary(riskTitleKey)];
                              form.setFieldsValue({
                                [keyName]: value,
                              });
                              if (customFieldsValueRef) {
                                customFieldsValueRef.current = {
                                  ...customFieldsValueRef.current,
                                  ...{ [keyName]: value },
                                };
                              }
                            }
                          }
                        }

                        if (isSeverityValueField && !isEmpty(obj.mappings)) {
                          for (const mappingKey in obj.mappings) {
                            const keyName = `${obj.key}-${mappingKey}`;
                            const value = obj.mappings[mappingKey];
                            form.setFieldsValue({
                              [keyName]: value,
                            });
                            if (customFieldsValueRef) {
                              customFieldsValueRef.current = {
                                ...customFieldsValueRef.current,
                                ...{ [keyName]: value },
                              };
                            }
                          }
                        }
                      }
                      // isEmpty considers number, boolean types as empty
                      if (
                        !isEmpty(_value) ||
                        (_value !== "" && !isNil(_value))
                      ) {
                        form.setFieldsValue({
                          [obj.key]: _value,
                        });
                        // when project key or issue type changes
                        // for Azure board don't set custom field value if it is an Area or Iteration field
                        if (
                          getIntegrationName(toolName) === "Azure Board" &&
                          (obj.name === "Area" || obj.name === "Iterations") &&
                          // TODO: resolve TS error: resolution pending till enhancing custom field code
                          // @ts-expect-error
                          initialData.current?.projectKey !== projectKey
                        ) {
                          form.setFieldsValue({ [obj.key]: undefined });
                        }
                      }
                    }
                  });
                }
                setCustomFieldState(customState);
                //TODO: need to resolve typesc
                // @ts-expect-error
                setInputCustomFields(fields);
              }
            })
            .finally(() => setLoading(false));
          return cancel;
        } else {
          resetFields();
        }
      }
    }, [
      customFieldsValueRef,
      loginConfigId,
      ticketConfigurationId,
      form,
      initialValues,
      setInputCustomFields,
      setCustomFieldState,
      apiParamRef,
      changeMode,
      configId,
      resetFields,
      innerRef,
      useValueField,
      toolName,
      featureFlags.TICKETING_CUSTOM_SEVERITY_RISK_RATING,
    ]);

    const handleCheckedChange = useCallback((checked, key) => {
      setCustomFieldState((prevState) => ({
        ...prevState,
        [key]: checked,
      }));
    }, []);

    useEffect(() => {
      form.validateFields(_.keys(customFieldState));
    }, [form, customFieldState]);

    const getApiParams = useCallback(
      (
        loginConfigId,
        projectKey,
        fieldLabel,
        dataType?,
        type?,
        issueTypeId?,
        useId = false,
      ) => {
        const newApiParams = {
          loginConfigId,
          projectKey,
          ...(useId ? { id: fieldLabel } : { fieldLabel }),
          type,
          dataType,
          ...(issueTypeId && { issueTypeId }),
        };
        //TODO: need to resolve typesc
        // @ts-expect-error
        if (!_.isEqual(apiParamRef.current[fieldLabel], newApiParams)) {
          //TODO: need to resolve typesc
          // @ts-expect-error
          apiParamRef.current[fieldLabel] = newApiParams;
        }
        //TODO: need to resolve typesc
        // @ts-expect-error
        return apiParamRef.current[fieldLabel];
      },
      [],
    );

    const generateCustomFields = useCallback(
      (fields, projectKey, toolName?: string) => {
        //TODO: need to resolve typesc
        // @ts-expect-error
        const customFields = fields.map((customField) => {
          const isEnabled =
            //TODO: need to resolve typesc
            // @ts-expect-error
            customFieldState?.[customField.key] ?? customField.active;
          const rules = [];
          if (
            customField.dataType === "number" ||
            ["integer", "float", "double", "decimal"].indexOf(
              customField.type,
            ) !== -1
          ) {
            rules.push({
              validator: (_rule: any, value: string) => {
                if (value) {
                  if (value.indexOf("${") !== -1) {
                    return findingFieldValidator(_rule, value);
                  } else {
                    return !_.isNaN(+value)
                      ? Promise.resolve()
                      : Promise.reject("Not a number");
                  }
                }
                return Promise.resolve();
              },
            });
          }
          const field = {
            name: customField.key,
            title: customField.name,
            isMapped: !_.isEmpty(customField.defaultVal),
            requireField: customField.isRequired, //useed requireField ,since required key validation from form utils
            rules: [
              {
                required: customField.isRequired || isEnabled,
                message: <>{customField.name} is required</>,
              },
              ...rules,
            ],
            switchProps: {
              checked: customField.isRequired || isEnabled,
              onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                handleCheckedChange(e, customField.key),
              disabled: customField.isRequired,
            },
            props: {},
            // check lowercase too, since JIRA has datetime where as Azure has dateTime
            //TODO: need to resolve typesc
            type:
              // @ts-expect-error
              CUSTOM_FIELD_TYPE[customField.type] ||
              // @ts-expect-error
              CUSTOM_FIELD_TYPE[customField.type.toLowerCase()],
            placeholder: customField.name,
          };

          if (field.type === FieldTypes.TEXT_AREA) {
            //TODO: need to resolve typesc
            // @ts-expect-error
            field.NoDefaultLengthRule = true;
          }

          //get options for subcategory and item category fields
          //as per BE response for FreshService, category field stores the nested options
          if (!isEmpty(categoryFieldRef.current)) {
            if (customField.name === "category") {
              field.props = {
                onChange: (value: string) => {
                  setSelectedFRCategory(value);
                  form.setFieldsValue({
                    [categoryFieldRef.current.nestedFields.key]: "",
                    [categoryFieldRef.current.nestedFields.nestedFields.key]:
                      "",
                  });
                },
              };
            }
            if (FR_CUSTOM_CATEGORY_FIELDS.includes(customField.name)) {
              const _selectedCategory =
                selectedFRCategory || categoryFieldRef.current.defaultVal;
              if (customField.name === "sub_category") {
                field.props = {
                  onChange: (value: string) => {
                    setSelectedFRSubCategory(value);
                    form.setFieldsValue({
                      [categoryFieldRef.current.nestedFields.nestedFields.key]:
                        "",
                    });
                  },
                };
                const { subCategoryOptions } =
                  getFreshServiceSubcategoryFieldOptions(
                    categoryFieldRef.current,
                    _selectedCategory,
                  ) || {};
                customField.allowedValues = subCategoryOptions;
              } else {
                const selectedSubCategory =
                  selectedFRSubCategory ||
                  categoryFieldRef.current.nestedFields.defaultVal;
                const { subCategoryItemOptions } =
                  getFreshServiceSubcategoryFieldOptions(
                    categoryFieldRef.current,
                    _selectedCategory,
                    selectedSubCategory,
                  ) || {};
                customField.allowedValues = subCategoryItemOptions;
              }
            }
          }

          if (CUSTOM_FIELDS_WITH_OPTIONS.includes(customField.type)) {
            //TODO: need to resolve typesc
            // @ts-expect-error
            field.options = customField.allowedValues;
            //zendesk requires name as value
            if (toolName?.toLowerCase() === "zendesk") {
              //TODO: need to resolve typesc
              // @ts-expect-error
              field.options = field.options.map((o: any) => ({
                label: o.name,
                value: o.name,
              }));
            }
            if (
              customField.type === "multicheckboxes" ||
              customField.type === "radiobuttons"
            ) {
              //TODO: need to resolve typesc
              // @ts-expect-error
              field.options = field.options.map((option) => {
                return {
                  label: <span title={option.name}>{option.name}</span>,
                  value: option.id,
                  ...option,
                };
              });
            }
            if (PROVIDE_SELECTED_VALUES.includes(customField.type)) {
              field.props = {
                ...(field.props || {}),
                labelInValue: true,
              };
            }
          }

          if (CUSTOM_FIELDS_WITH_SEARCH.includes(customField.type)) {
            // temporary rare use case
            // archer has too many fields which call the field suggestion API,
            // due to API race condition in Archer system....
            // it somehow blocks multiple API calls
            // call field suggestion API if only a field is active for Archer
            const isFieldEnabledOnUI = customField.isRequired || isEnabled;
            const enableFieldSuggestionAPICall =
              toolName?.toLowerCase() === "archer" ? isFieldEnabledOnUI : true;
            const isReferenceType = customField.type === "reference";
            // TODO: improve tool specific logic
            const useId = toolName?.toLowerCase() === "archer";
            const useOrganisation = toolName?.toLowerCase() === "asana";
            if (
              (isReferenceType && !_.isEmpty(customField.dataType)) ||
              !isReferenceType
            ) {
              if (isReferenceType) {
                field.type = FieldTypes.SELECT_WITH_PAGINATED_DATA;
              }
              const apiParams = {
                ...getApiParams(
                  loginConfigId,
                  projectKey,
                  useId
                    ? customField.key
                    : isReferenceType
                      ? customField.dataType
                      : customField.name,
                  customField.dataType,
                  customField.type,
                  initialValues?.issueTypeId,
                  useId,
                ),
                ...(runbook
                  ? { configId }
                  : { issueType: initialValues.issueType }),
              };
              if (useOrganisation) {
                apiParams.organisation = initialValues?.organisation;
              }
              field.props = {
                mode: CUSTOM_FIELDS_SINGLE_SELECTION.includes(customField.type)
                  ? "single"
                  : "multiple",
                parseSelectOptions: (
                  options: { id: number | string; name: string }[],
                ) => {
                  if (!isEmpty(customField.selectedValues)) {
                    options = _.uniqBy(
                      [...options, ...customField.selectedValues],
                      "id",
                    );
                  }
                  // TS resolve: Type '{ label: string; value: any; }[]' is not assignable to type '{ id: string | number; name: string; }[]'
                  // @ts-expect-error
                  options = stripHtml(options);
                  return options;
                },
                ...(enableFieldSuggestionAPICall
                  ? { api: TicketsCreationAPI.getCustomTicketFieldsSuggestions }
                  : {}),
                apiParams,
                labelInValue: true,
              };
            }
          }
          if (CUSTOM_FIELDS_WITH_USER_API.includes(customField.type)) {
            field.props = {
              ...(customField.type === "multiuserpicker" && {
                mode: "multiple",
              }),
              apiParams: getApiParams(
                loginConfigId,
                projectKey,
                customField.name,
              ),
              api: ToolsIntegrationsAPI.getTicketingUsers,
              parseSelectOptions: (
                options: { id: number | string; name: string }[],
              ) => {
                if (!isEmpty(customField.selectedValues)) {
                  options = _.uniqBy(
                    [...options, ...customField.selectedValues],
                    "id",
                  );
                }
                return _.map(options, (o) => ({
                  label: o.name,
                  value: o.id,
                }));
              },
              labelInValue: true,
            };
          }
          // check type in lowercase, since JIRA has datetime and Azure has dateTime
          if (
            customField.type.toLowerCase() === "datetime" ||
            customField.type === "datepicker"
          ) {
            field.props = {
              disabledDate: disablePastDates,
            };
          }

          if (TIME_SUPPORTED_DATE_FIELDS.indexOf(customField.type) !== -1) {
            field.props = {
              showTime: true,
              disabledDate: disablePastDates,
            };
          }

          if (
            customField.name === "Area" ||
            customField.name === "Iterations"
          ) {
            // TODO: need to resolve typesc
            // @ts-expect-error
            field.render = () => (
              <TreeSelect
                showSearch
                dropdownStyle={{
                  maxHeight: 400,
                }}
                style={{ width: "80%" }}
                allowClear
                treeData={parseAreaOptions(customField.allowedValues)}
                virtual={false}
              />
            );
          }

          // check type in lowercase, since JIRA has datetime and Azure has dateTime
          if (
            (customField.type === "datepicker" ||
              customField.type.toLowerCase() === "datetime") &&
            !_.isEmpty(initialValues[customField.key]) &&
            !moment(initialValues[customField.key]).isValid()
          ) {
            field.type = undefined;
            //TODO: need to resolve typesc
            // @ts-expect-error
            field.render = () => (
              <Tooltip title="Template string is selected. Please clear template first to select date.">
                <span>
                  <Input value={customField.defaultVal} disabled />
                </span>
              </Tooltip>
            );
          }

          if (customField.type === "HOUR_AND_MINUTE_STRING") {
            field.rules.push({
              //TODO: need to resolve typesc
              validator: (rule, value) => {
                if (!isEmpty(value)) {
                  if (!/^\d+h \d+m$/.test(value)) {
                    return Promise.reject(
                      "Valid format for Planned effort is 1h 2m",
                    );
                  }
                }
                return Promise.resolve();
              },
            });
          }
          return field;
        });
        return customFields;
      },
      [
        customFieldState,
        initialValues,
        handleCheckedChange,
        getApiParams,
        loginConfigId,
        selectedFRCategory,
        selectedFRSubCategory,
        form,
      ],
    );
    const customField = useMemo(() => {
      return generateCustomFields(
        inputCustomFields,
        initialValues.projectKey ?? initialValues.form,
        toolName,
      );
    }, [
      inputCustomFields,
      initialValues.projectKey,
      generateCustomFields,
      initialValues.form,
      toolName,
    ]);

    const renderFields = useCallback(
      (customFields, toolName) => {
        if (renderCustomFields && typeof renderCustomFields === "function") {
          return renderCustomFields(customFields, onValuesChange);
        }
        return generateJiraCustomFields({
          customFields,
          form,
          onValuesChange,
          toolName,
        });
      },
      [renderCustomFields, form, onValuesChange],
    );

    useImperativeHandle(ref, () => ({
      getCustomFields: () => ({ inputCustomFields, customFieldState }),
    }));

    const WrapperComponent = useMemo(() => {
      const collapseProps: any = {};
      const CustomForm = (
        <Form
          onValuesChange={onValuesChange}
          form={form}
          layout="vertical"
          initialValues={initialData.current}
        >
          <Spin
            spinning={loading}
            indicator={<AppLoader style={{ marginTop: 0 }} />}
          >
            {_.isEmpty(customField)
              ? text
              : renderFields(customField, toolName)}
          </Spin>
        </Form>
      );
      const Wrapper = withCollapse ? CustomCollapse : CustomForm;
      if (withCollapse) {
        collapseProps.header = (
          <>
            Configure Custom Fields{" "}
            <Tooltip
              title={
                // @ts-expect-error
                restProps.tooltip ??
                "By enabling the toggles, only those fields will be mapped to your project and the rest will be ignored. A text area and drop-down boxes allow you to add default values. These values will be considered every time you create a ticket."
              }
            >
              {" "}
              <InfoIcon />
            </Tooltip>
            {toolName?.toLowerCase() == "asana" && (
              <div className="m-t-m">
                <Alert
                  description="Projects and custom fields are refreshed every 24 hours. If you recently added or modified projects or custom fields in Asana, changes may take up to 24 hours to appear here."
                  type="info"
                  showIcon
                />
              </div>
            )}
          </>
        );
        collapseProps.type = "info";
        collapseProps.description = CustomForm;
        collapseProps.Key = "custom_field";
        collapseProps.defaultActiveKey = "custom_field";
        collapseProps.props = { destroyInactivePanel: true };
        collapseProps.isCollapseDisabled = customField.some(
          (field: { name: string; requireField: boolean }) =>
            field.requireField,
        );
      }
      //TODO: need to resolve typesc
      // @ts-expect-error
      return <Wrapper {...collapseProps} />;
    }, [
      toolName,
      withCollapse,
      form,
      initialData,
      customField,
      renderFields,
      text,
      loading,
      onValuesChange,
      // @ts-expect-error
      restProps.tooltip,
    ]);

    return WrapperComponent;
  },
);

export function stripHtml(options: any) {
  return _.map(options, (option) => {
    const div = document.createElement("div");
    div.innerHTML = option.name;
    const text = div.textContent ?? div.innerText ?? "";
    return { label: text, value: option.id };
  });
}

const getFreshServiceSubcategoryFieldOptions = (
  category: any,
  selectedCategory: any,
  selectedSubCategory?: any,
): { [key: string]: any } => {
  let subCategoryOptions = [];
  if (!isEmpty(category.allowedValues)) {
    subCategoryOptions = category.allowedValues?.find(
      (value: any) => value.id === selectedCategory,
    )?.allowedValues;
    const subCategoryItemOptions = subCategoryOptions?.find(
      (value: any) => value.id === selectedSubCategory,
    )?.allowedValues;
    return { subCategoryOptions, subCategoryItemOptions };
  }
  return {};
};

export const getFreshServiceCustomNestedFields = (field: any): any[] => {
  let nestedFields = [];

  if (field.nestedFields) {
    // Add the nested field object itself to the array
    nestedFields.push(field.nestedFields);

    // Recursively call the function to gather any deeper nested fields
    nestedFields = nestedFields.concat(
      getFreshServiceCustomNestedFields(field.nestedFields),
    );
  }

  return nestedFields;
};

export const getFreshServiceNestedFieldOptionsByCategory = (
  data: any[],
  categoryId: string,
  subcategoryId: string,
  categoryOptions: any[] = [],
) => {
  const result: any[] = [];
  let allOptionsFound = false;
  function findAndCollectNestedOptions(items: any[], id: string) {
    for (const item of items) {
      if (item.id === id) {
        if (item.allowedValues) {
          result.push(item.allowedValues);
          return item.allowedValues; // Continue searching within the allowedValues
        }
      }
    }
    return null; // Return null if no match is found
  }

  function recursiveSearchByCategories(
    items: any[],
    categoryId: string,
    subcategoryId: string,
  ) {
    //stop calling function if all current levels has been searched and options extracted
    if (allOptionsFound) {
      return;
    }
    //this condition check means upper level nested field options found and exist in categoryOptions
    //and currently it is searching for deeper levels
    //search by subCategory in nested levels
    const searchCategory =
      categoryOptions.length > 1 ? subcategoryId : categoryId;
    const currentLevel = findAndCollectNestedOptions(items, searchCategory);
    if (currentLevel && subcategoryId) {
      allOptionsFound = true;
      findAndCollectNestedOptions(currentLevel, subcategoryId);
    }
  }
  //search within sub nested fields options
  if (categoryOptions.length > 1) {
    recursiveSearchByCategories(
      categoryOptions[categoryOptions.length - 1],
      categoryId,
      subcategoryId,
    );
  } else {
    //search within main array of options
    recursiveSearchByCategories(data, categoryId, subcategoryId);
  }

  if (result.length) {
    return _.compact(categoryOptions.concat(result));
  }
  return categoryOptions;
};

export const setupFreshServiceCustomNestedFields = (
  customFields: any[],
  selectedCategory?: string,
  selectedSubCategory?: string,
) => {
  let nestedFields: any[] = [];
  let nestedFieldsIndex;
  if (customFields?.length) {
    const fieldWithNestedFields = customFields.find((field, index) => {
      nestedFieldsIndex = index;
      return !isNil(field.nestedFields);
    });
    if (fieldWithNestedFields) {
      //get sub nested fields from main field consisting of sub nested fields
      //gets raw structure of sub nested fields
      nestedFields = getFreshServiceCustomNestedFields(fieldWithNestedFields);
      if (nestedFields.length) {
        //as sub nested fields options exist in multiple nested levels inside main array of options
        // @categoryOptions gets seeded with the nested or sub nested field options found within iteration
        // it will be used to filter and get options available within deeper levels
        const categoryOptions: any[] = [];
        const category = selectedCategory ?? fieldWithNestedFields.defaultVal;
        nestedFields = nestedFields.map((field: any, i: number) => {
          const subCategory = selectedSubCategory ?? field.defaultVal;
          categoryOptions.push(
            ...getFreshServiceNestedFieldOptionsByCategory(
              fieldWithNestedFields.allowedValues,
              category,
              subCategory,
              categoryOptions,
            ),
          );
          const fieldDefaultValue = {
            ...field.defaultValue,
            id: selectedSubCategory
              ? selectedSubCategory
              : field.defaultValue?.id,
          };
          return {
            ...field,
            defaultVal: subCategory,
            defaultValue: fieldDefaultValue,
            allowedValues: categoryOptions[i],
          };
        });
      }
    }
  }
  return [nestedFields, nestedFieldsIndex];
};
export const getFieldWithFindingVariablesSelect = ({
  field,
  form,
  onValuesChange,
  customFieldsValues,
  isFindingTicketPage = false,
  toolName,
}: {
  field: any;
  form: FormInstance;
  onValuesChange: (value: Record<string, string | undefined>) => void;
  customFieldsValues?: any;
  isFindingTicketPage?: boolean;
  toolName?: string;
}) => {
  const isVariable = _.startsWith(
    form.getFieldValue(field.name) ?? customFieldsValues?.[field.name],
    "${finding.",
  );
  const fieldValue =
    form.getFieldValue(field.name) ?? customFieldsValues?.[field.name];
  const isInvalidDate = !isEmpty(fieldValue) && !moment(fieldValue).isValid();
  const isRiskRatingField =
    fieldValue &&
    _.isString(fieldValue) &&
    fieldValue.indexOf("finding.riskRating") !== -1;
  const isSeverityValueField =
    fieldValue &&
    _.isString(fieldValue) &&
    fieldValue.indexOf("finding.severity.value") !== -1;

  const TICKETING_CUSTOM_SEVERITY_RISK_RATING =
    getFeatureFlags_UNSAFE().TICKETING_CUSTOM_SEVERITY_RISK_RATING;
  if (
    [FieldTypes.BOOLEAN, FieldTypes.RADIO, FieldTypes.CHECKBOX_GROUP].indexOf(
      field.type,
    ) !== -1
  ) {
    return [field];
  }

  const shouldDisableRiskRatingAndSeverityValueVariables =
    TICKETING_CUSTOM_SEVERITY_RISK_RATING &&
    isFindingTicketPage &&
    (isSeverityValueField || isRiskRatingField);

  const commonSeverityProps = {
    colSpan: 24 / 5,
    className: "m-b-0",
    type: FieldTypes.SELECT,
    rules: [
      {
        required: true,
        message: `Select Priority`,
      },
    ],
    options: field.options || [],
  };

  const {
    severityFieldCriticalName,
    severityFieldHighName,
    severityFieldMediumName,
    severityFieldLowName,
  } = getCustomSeverityMappingFieldNames(isRiskRatingField);

  const severityOrMappingFieldsText = isRiskRatingField
    ? "Risk Rating"
    : "Severity";
  return [
    {
      name: `${field.name}-parent`,
      className: "w-100-label",
      label: field.label,
      type: FieldTypes.INLINE_GROUP,
      fields: [
        {
          ...field,
          type:
            ([...ALL_DATE_FIELDS, ...TIME_SUPPORTED_DATE_FIELDS].includes(
              field.type?.toLowerCase(),
            ) &&
              isInvalidDate) ||
            isVariable
              ? undefined
              : field.type,
          props: {
            ...(field.props || {}),
            className: "select-input-group",
            style: {
              ...field.style,
              width: "100%",
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
            },
          },
          // below style is for input group Form.Item
          style: {
            width: "80%",
          },
        },
        {
          name: `${field.name}-variable-select`,
          label: "",
          render: () => (
            <ACDropdown
              items={
                // required for duedate fiel specific options
                field.name === "duedate"
                  ? CUSTOM_FIELDS_VARIABLE_FIELD_OPTIONS[
                      field.name as keyof typeof CUSTOM_FIELDS_VARIABLE_FIELD_OPTIONS
                    ]
                  : getCustomVariableFieldOptions(
                      shouldDisableRiskRatingAndSeverityValueVariables,
                    )
              }
              placement="topRight"
              getPopupContainer={() =>
                (document.querySelector(".jira-mapping-form .ant-modal-body") ??
                  document.body) as HTMLElement
              }
              onOptionClick={(option) => {
                const valueToSet =
                  option === "custom-value" ? undefined : (option as string);
                onValuesChange({ [field.name]: valueToSet });
                form?.setFieldsValue({
                  [field.name]: valueToSet,
                });
              }}
              trigger="click"
            >
              <Button
                style={{
                  padding: "0px 4px",
                  fontSize: 20,
                  borderRadius: 5,
                }}
              >
                <CustomFieldsIcon />
              </Button>
            </ACDropdown>
          ),
          props: {
            style: { width: 50 },
          },
        },
      ],
    },
    ...(TICKETING_CUSTOM_SEVERITY_RISK_RATING &&
    // @ts-expect-error
    CUSTOM_SEVERITY_MAPPING_SUPPORTED_TOOLS.includes(toolName?.toLowerCase()) &&
    !isFindingTicketPage &&
    !isEmpty(field.options) &&
    (isRiskRatingField || isSeverityValueField)
      ? [
          {
            label: (
              <span
                className="font-size-base"
                style={{ color: "#70747C", paddingBottom: 8 }}
              >
                Map Finding {severityOrMappingFieldsText} values to{" "}
                {capitalize(toolName)} {field.title} values
              </span>
            ),
            type: FieldTypes.GROUP,
            fields: [
              {
                name: `${field.name}-${severityFieldCriticalName}`,
                placeholder: `${capitalize(toolName)} Value`,
                label: isRiskRatingField ? (
                  <Vocabulary name="goodRiskScore" />
                ) : (
                  <Vocabulary name="criticalName" />
                ),
                ...commonSeverityProps,
              },
              {
                name: `${field.name}-${severityFieldHighName}`,
                placeholder: `${capitalize(toolName)} Value`,
                label: isRiskRatingField ? (
                  <Vocabulary name="fairRiskScore" />
                ) : (
                  <Vocabulary name="highName" />
                ),
                ...commonSeverityProps,
              },
              {
                name: `${field.name}-${severityFieldMediumName}`,
                placeholder: `${capitalize(toolName)} Value`,
                label: isRiskRatingField ? (
                  <Vocabulary name="poorRiskScore" />
                ) : (
                  <Vocabulary name="mediumName" />
                ),
                ...commonSeverityProps,
              },
              {
                name: `${field.name}-${severityFieldLowName}`,
                placeholder: `${capitalize(toolName)} Value`,
                label: isRiskRatingField ? (
                  <Vocabulary name="veryPoorRiskScore" />
                ) : (
                  <Vocabulary name="lowName" />
                ),
                ...commonSeverityProps,
              },
              ...(isSeverityValueField
                ? [
                    {
                      name: `${field.name}-info`,
                      placeholder: `${capitalize(toolName)} Value`,
                      label: <Vocabulary name="infoName" />,
                      ...commonSeverityProps,
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
  ];
};

export const useTemplateSupportedIntegrations = () => {
  const featureFlags = useFeatureFlag();
  return useMemo(
    () =>
      _.compact(
        _.map(TEMPLATE_SUPPORTED_INTEGRATIONS, (entry) => {
          if (!entry.featureFlagCheck || entry.featureFlagCheck(featureFlags)) {
            return entry.tool;
          }
          return null;
        }),
      ),
    [featureFlags],
  );
};

export const useCmdbSupportedIntegrations = (toolName: string) => {
  const featureFlags = useFeatureFlag();
  return useMemo(
    () =>
      _.compact(
        _.map(CMDB_SUPPORTED_TOOLS, (entry) => {
          if (
            (entry.tool === toolName && entry.featureFlagCheck(featureFlags)) ||
            !entry.featureFlagCheck
          ) {
            return entry.tool;
          }
          return null;
        }),
      ),
    [featureFlags, toolName],
  );
};

/**
 * used to name the form fields corresponding to ArmorCode severity fields
 * in custom fields.
 *
 * - If the field is not a Risk Rating (i.e., the custom field value contains the
 *   `${finding.severity.value}` variable), it returns one of the severity levels:
 *   Critical, High, Medium, or Low.
 *
 * - If the field is a Risk Rating (i.e., the custom field value contains the
 *   `${finding.riskRating}` variable), it returns the Risk Score measuring titles from Global Settings:
 *   Good, Fair, Poor, or Very Poor.
 */
export const getCustomSeverityMappingFieldNames = (
  isRiskRatingField: boolean,
) => {
  const severityFieldCriticalName = isRiskRatingField
    ? getVocabulary("goodRiskScore")
    : "critical";
  const severityFieldHighName = isRiskRatingField
    ? getVocabulary("fairRiskScore")
    : "high";
  const severityFieldMediumName = isRiskRatingField
    ? getVocabulary("poorRiskScore")
    : "medium";
  const severityFieldLowName = isRiskRatingField
    ? getVocabulary("veryPoorRiskScore")
    : "low";
  return {
    severityFieldCriticalName,
    severityFieldHighName,
    severityFieldMediumName,
    severityFieldLowName,
  };
};

export const getSeverityFieldMappingsPayload = (
  customField: any,
  formData: any,
) => {
  const isRiskRatingField =
    formData[customField.key] &&
    _.isString(formData[customField.key]) &&
    formData[customField.key].indexOf("finding.riskRating") !== -1;

  const isSeverityValueField =
    formData[customField.key] &&
    _.isString(formData[customField.key]) &&
    formData[customField.key].indexOf("finding.severity.value") !== -1;

  const mappings: any = {};

  const mappingKeys = isSeverityValueField
    ? Object.keys(SEVERITY_NAMES)
    : isRiskRatingField
      ? _.map(CUSTOM_FIELDS_RISK_RATING_MAPPING_KEYS, (key) =>
          getVocabulary(key),
        )
      : [];

  mappingKeys.forEach((mappingKey) => {
    if (formData[`${customField.key}-${mappingKey}`]) {
      mappings[customField.key] = {
        ...mappings[customField.key],
        [mappingKey]: formData[`${customField.key}-${mappingKey}`],
      };
    }
  });

  if (!isEmpty(mappings[customField.key])) {
    return mappings[customField.key];
  }

  return {};
};

export const getCustomVariableFieldOptions = (
  shouldDisableVariable: boolean,
  shouldDisplayCustomValue: boolean = true,
): ACDropdownItemProps[] => {
  return [
    ...(shouldDisplayCustomValue
      ? [{ value: "custom-value", label: "Custom Value" }]
      : []),
    ...templateFieldsWithBrackets.map((field) => ({
      label: field,
      value: field,
      disabled:
        shouldDisableVariable &&
        (field.indexOf("finding.riskRating") !== -1 ||
          field.indexOf("finding.severity.value") !== -1),
    })),
  ];
};

export const getZendeskProjectKeyLabel = (data: any) => {
  if (!isEmpty(data)) {
    let label = `${data.organisationName}/${data.formName}`;
    const subProductNames = !isEmpty(data.subProductNameIds)
      ? _.join(_.map(data.subProductNameIds, (sp) => sp.name))
      : "All";
    const productNames = !isEmpty(data.productNameId)
      ? _.join(_.map(data.productNameId, (p) => p.name))
      : "All";
    if (data.issueType) {
      label += ` (${data.issueType})`;
    }

    label += ` : ${productNames}-${subProductNames}`;

    return label;
  }
  return "";
};

export const getJiraCmdbCustomAttributesFields = ({
  customAttributesFields,
}: any) => {
  if (isEmpty(customAttributesFields)) {
    return null;
  }

  const ipAddressTooltipe =
    "These will be used as asset matching criteria against the findings";
  const hostNameTooltipe =
    "These will be used for asset matching criteria against the findings. Default value will be the asset name";
  return (
    <>
      <Row className={`jira-custom-field custom-field-header`}>
        <Col span={6}>
          <span>Asset Attribute</span>
        </Col>
        <Col span={6}>
          <span>Jira CMDB Field</span>
        </Col>
      </Row>
      {customAttributesFields.map((field: any, i: number) => {
        const showToolTip =
          field.name === "ipAddress" || field.name === "hostname";
        return (
          <>
            <Row
              className={classNames("jira-custom-field", {
                [`custom-field-${i}`]: true,
              })}
            >
              <Col span={11}>
                <div
                  className={classNames("title text-bold m-b-xs", {
                    "required-mark": field.requireField,
                  })}
                >
                  {field.title}{" "}
                  {showToolTip && (
                    <Tooltip
                      title={
                        field.name === "ipAddress"
                          ? ipAddressTooltipe
                          : hostNameTooltipe
                      }
                    >
                      <InfoIcon />
                    </Tooltip>
                  )}
                </div>
                {field.description && (
                  <div className="description">{field.description}</div>
                )}
              </Col>
              {getField(field, i, 12)}
            </Row>
          </>
        );
      })}
    </>
  );
};

export const SyncCMDBResourceButton = ({
  toolName,
  isDisabled,
}: {
  toolName: string;
  isDisabled: boolean;
}) => {
  const syncRepos = useCallback(async (toolName: string) => {
    try {
      await ToolsIntegrationsAPI.syncAsset(
        _.toUpper(toolName.replace(" ", "_")),
      ).promise;
      message.success("Syncing assets, it may take some time.");
    } catch (e) {
      console.log(e);
    }
  }, []);
  return (
    <AuthorisedContainer permission={PermissionSetMap.WRITE_TOOLS}>
      <Button
        type="default"
        size="small"
        className="m-l-auto m-y-sm"
        style={{ display: "block" }}
        onClick={() => syncRepos(toolName)}
        disabled={isDisabled}
      >
        <RefreshIcon /> Sync CMDB Resources
      </Button>
    </AuthorisedContainer>
  );
};

export const integrationTicketingToolsProductOptionsMutator = (
  options: [{ label: string; value: number | string }],
  selectedProducts: (number | string)[],
) => {
  const updatedOptions =
    genericIntegrationTicketProductSubproductAppendAll(options);
  const isAllSelected = selectedProducts.includes("all");
  return updatedOptions.map((option) => {
    const disableAllOption =
      !isEmpty(selectedProducts) && !isAllSelected && option.value === "all";
    return {
      ...option,
      disabled: (isAllSelected && option.value !== "all") || disableAllOption,
    };
  });
};

// TODO: use this function to enhance current logic of Fresh Service code for extracting nestedFields
export const getCustomFieldNestedFields = (
  customFields: IntegrationCustomFieldTypes[],
) => {
  return customFields.flatMap((field) => {
    const extractedNestedFields = [];
    let currentNestedField = field.nestedFields;
    while (!isEmpty(currentNestedField)) {
      extractedNestedFields.push(currentNestedField);
      currentNestedField = currentNestedField.nestedFields;
    }
    return extractedNestedFields;
  });
};

export const getCustomFieldSelectedValuesPayload = (
  data: GenericIntegrationTicketDefaultValues,
  customField: IntegrationCustomFieldTypes,
) => {
  const customFieldKey =
    customField.key! as keyof GenericIntegrationTicketDefaultValues;
  let selectedValues = [];

  // when mapping is saved directly without changing the field
  // the initial value of the field(data[cstmField.key]) is [ids of previously selected values]
  const formFieldValues = isArray(data[customFieldKey])
    ? data[customFieldKey]
    : // when dropdown is single select
      [data[customFieldKey]];
  selectedValues = _.every(formFieldValues as LabelValueType[], (v) => v.label)
    ? _.map(formFieldValues as LabelValueType[], (d) => ({
        id: d.value,
        name: d.label,
      }))
    : // set previously selectedValue for payload
      (customField.selectedValues as IdNameType[]);
  const fieldValue = _.map(selectedValues, (value) => value.id).join();
  return { fieldValue, selectedValues };
};
